// UserDashboard.jsx - Modern Apple-like design
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';
import { logUserUpload } from '../utils/supabaseUtils';
import DashboardEditor from './DashboardEditor';
import './UserDashboard.css';

const removeBgApiKey = import.meta.env.VITE_REMOVE_BG_API_KEY;

const UserDashboard = () => {
  // Editor state
  const [cesdk, setCesdk] = useState(null);
  
  // State variables
  const [user, setUser] = useState(null);
  const [sourceImages, setSourceImages] = useState([]);
  const [primaryImage, setPrimaryImage] = useState(null);
  const [uploadedImageURL, setUploadedImageURL] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [exportedImage, setExportedImage] = useState(null);
  const [placeholderFields, setPlaceholderFields] = useState([]);
  // State for CESDK placeholders
  const [placeholderData, setPlaceholderData] = useState([]);
  const [showTemplateBrowser, setShowTemplateBrowser] = useState(false);
  // State for saved custom images
  const [savedImages, setSavedImages] = useState([]);
  // State for saved export
  const [currentExport, setCurrentExport] = useState(null);
  // State for image gallery
  const [galleryVisible, setGalleryVisible] = useState(false);
  const [currentGalleryImage, setCurrentGalleryImage] = useState(null);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  
  const navigate = useNavigate();

  // CESDK editor ready handler
  const handleEditorReady = (cesdkInstance) => {
    setCesdk(cesdkInstance);
  };

  // Monitor CESDK initialization
  useEffect(() => {
    if (cesdk) {
      console.log("CESDK initialized successfully");
    }
  }, [cesdk]);

  // Fetch user session and data
  useEffect(() => {
    const fetchSessionAndData = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error || !data.session) {
        navigate('/login');
        return;
      }
      setUser(data.session.user);

      // First, get active uploads from the database
      const { data: uploads, error: uploadError } = await supabase
        .from('user_uploads')
        .select('file_url, is_primary')
        .eq('user_id', data.session.user.id)
        .eq('status', 'active');

      if (uploadError) {
        console.error('Error fetching user uploads:', uploadError);
        setError(uploadError.message);
      } else {
        // Get the active file URLs
        const activeFileUrls = uploads.map(upload => upload.file_url);
        
        // Find primary image
        const primary = uploads.find(upload => upload.is_primary);
        setPrimaryImage(primary ? primary.file_url : null);

        // Fetch background-removed images
        const { data: images, error: imgError } = await supabase.storage
          .from('playmakerpics-images')
          .list(`background_removed_images/${data.session.user.id}`, {
            limit: 100,
            offset: 0,
            sortBy: { column: 'name', order: 'asc' },
          });

        if (imgError) {
          console.error('Error fetching source images:', imgError);
          setError(imgError.message);
        } else {
          // Only include images that are in the active uploads
          const formattedImages = images
            .filter(file => {
              const fileUrl = supabase.storage
                .from('playmakerpics-images')
                .getPublicUrl(`background_removed_images/${data.session.user.id}/${file.name}`)
                .data.publicUrl;
              return file.name !== '.emptyFolderPlaceholder' && activeFileUrls.includes(fileUrl);
            })
            .map(file => ({
              name: file.name,
              url: supabase.storage
                .from('playmakerpics-images')
                .getPublicUrl(`background_removed_images/${data.session.user.id}/${file.name}`)
                .data.publicUrl,
            }));
          setSourceImages(formattedImages);

          // If no primary image was set from the database, use the first active image
          if (!primaryImage && formattedImages.length > 0) {
            setPrimaryImage(formattedImages[0].url);
          }
        }
      }

      // Fetch templates
      const { data: templateData, error: templateError } = await supabase
        .from('templates')
        .select('id, name, thumbnail_url, placeholder_data, scene_url, style_category, style_subcategory')
        .order('updated_at', { ascending: false });
      if (templateError) {
        console.error('Error fetching templates:', templateError);
        setError(templateError.message);
      } else {
        setTemplates(templateData || []);
      }
      
      // Fetch saved custom images
      const { data: exportData, error: exportError } = await supabase
        .from('exports')
        .select('id, template_id, export_url, exported_at, is_saved')
        .eq('user_id', data.session.user.id)
        .eq('is_saved', true)
        .order('exported_at', { ascending: false });
        
      if (exportError) {
        console.error('Error fetching saved exports:', exportError);
      } else {
        // Map export data to include template name if available
        const savedExports = exportData?.map(export_item => {
          const template = templateData?.find(t => t.id === export_item.template_id);
          return {
            ...export_item,
            template_name: template ? template.name : 'Custom Design'
          };
        }) || [];
        
        setSavedImages(savedExports);
      }
    };
    
    fetchSessionAndData();
  }, [navigate, primaryImage]);

  // Handle file upload with background removal
  const handleFileChange = async (event) => {
    if (!user) {
      setError('Please log in to upload an image');
      return;
    }

    setIsLoading(true);
    setError(null);
    const file = event.target.files[0];
    if (!file) {
      setIsLoading(false);
      return;
    }

    try {
      // Process with remove.bg API
      const formData = new FormData();
      formData.append('image_file', file);

      const response = await fetch('https://api.remove.bg/v1.0/removebg', {
        method: 'POST',
        headers: { 'X-API-Key': removeBgApiKey },
        body: formData,
      });

      if (!response.ok) throw new Error(`Remove.bg API error: ${await response.text()}`);

      // Create a blob from the response
      const blob = await response.blob();
      const processedFile = new File([blob], `processed-${Date.now()}.png`, { type: 'image/png' });

      // Upload to Supabase storage
      const filePath = `background_removed_images/${user.id}/${processedFile.name}`;
      const { error: uploadError } = await supabase.storage
        .from('playmakerpics-images')
        .upload(filePath, processedFile, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { publicUrl } = supabase.storage
        .from('playmakerpics-images')
        .getPublicUrl(filePath).data;

      // Log upload and update state
      const isFirstImage = sourceImages.length === 0;
      await logUserUpload(user.id, publicUrl, isFirstImage);
      setUploadedImageURL(publicUrl);
      const newImage = { name: processedFile.name, url: publicUrl };
      setSourceImages([...sourceImages, newImage]);
      if (isFirstImage) setPrimaryImage(publicUrl);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete an image
  const handleDeleteImage = async (imageName) => {
    try {
      console.log('=== Starting Image Deletion Process ===');
      console.log('1. Initial Parameters:', { imageName, userId: user?.id });
      
      // First, get the file path and URL
      const filePath = `background_removed_images/${user.id}/${imageName}`;
      const imageUrl = supabase.storage.from('playmakerpics-images').getPublicUrl(filePath).data.publicUrl;
      console.log('2. File Details:', { filePath, imageUrl });

      // Log current state before deletion
      console.log('3. Current State:', {
        sourceImagesCount: sourceImages.length,
        primaryImage,
        isPrimaryImage: primaryImage === imageUrl
      });

      // Check if the upload exists in the database first
      console.log('4. Checking if upload exists in database...');
      const { data: existingUpload, error: checkError } = await supabase
        .from('user_uploads')
        .select('*')
        .eq('user_id', user.id)
        .eq('file_url', imageUrl)
        .single();

      if (checkError) {
        console.error('❌ Error checking upload:', checkError);
        throw checkError;
      }

      if (!existingUpload) {
        console.error('❌ No matching upload found in database');
        throw new Error('No matching upload found in database');
      }

      console.log('5. Found existing upload:', existingUpload);

      // Update the status in the user_uploads table to 'deleted'
      console.log('6. Attempting database update...');
      const { data, error: dbError } = await supabase
        .from('user_uploads')
        .update({ status: 'deleted' })
        .eq('id', existingUpload.id)  // Use the exact ID we found
        .select();

      if (dbError) {
        console.error('❌ Database update failed:', dbError);
        throw dbError;
      }
      console.log('✅ Database update result:', data);

      // Update UI state by filtering out the deleted image
      console.log('7. Updating UI state...');
      const updatedImages = sourceImages.filter(img => img.name !== imageName);
      setSourceImages(updatedImages);
      console.log('Updated sourceImages count:', updatedImages.length);

      // If we deleted the primary image, update the primary image
      if (primaryImage === imageUrl) {
        console.log('8. Primary image was deleted, updating primary image...');
        // Refetch the active uploads to get the most up-to-date data
        const { data: activeUploads } = await supabase
          .from('user_uploads')
          .select('file_url, is_primary')
          .eq('user_id', user.id)
          .eq('status', 'active');
        
        console.log('Active uploads after deletion:', activeUploads);
        
        const newPrimary = activeUploads?.find(upload => upload.is_primary);
        if (newPrimary) {
          console.log('Setting new primary from database:', newPrimary.file_url);
          setPrimaryImage(newPrimary.file_url);
        } else if (updatedImages.length > 0) {
          console.log('No primary in DB, using first active image:', updatedImages[0].url);
          setPrimaryImage(updatedImages[0].url);
        } else {
          console.log('No images remaining, setting primary to null');
          setPrimaryImage(null);
        }
      }

      console.log('=== Image Deletion Process Completed Successfully ===');
    } catch (err) {
      console.error('❌ Error in handleDeleteImage:', err);
      setError(err.message);
    }
  };

  // Set primary image
  const handleSetPrimary = async (url) => {
    try {
      setPrimaryImage(url);
      await supabase
        .from('user_uploads')
        .update({ is_primary: false })
        .eq('user_id', user.id);
      await supabase
        .from('user_uploads')
        .update({ is_primary: true })
        .eq('user_id', user.id)
        .eq('file_url', url);
    } catch (err) {
      console.error('Error setting primary image:', err);
      setError(err.message);
    }
  };

  // Handle template customization with CESDK
  const handleCustomizeTemplate = async (template) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Check if CESDK is initialized
      if (!cesdk) {
        throw new Error("Editor is not initialized. Please refresh the page and try again.");
      }
      
      // Load the template scene
      if (!template.scene_url) {
        throw new Error("Template has no scene URL");
      }
      
      // Fetch scene data
      const response = await fetch(template.scene_url);
      if (!response.ok) {
        throw new Error(`Failed to fetch scene: ${response.statusText}`);
      }
      
      const sceneText = await response.text();
      if (!sceneText.startsWith('UBQ')) {
        throw new Error('Invalid scene data format');
      }
      
      // Load the scene - try different methods for compatibility
      let loadSuccess = false;
      
      // Method 1: Direct loadFromString
      if (!loadSuccess && typeof cesdk.loadFromString === 'function') {
        try {
          await cesdk.loadFromString(sceneText);
          loadSuccess = true;
        } catch (err) {
          console.warn("Failed loading with direct method:", err.message);
        }
      }
      
      // Method 2: Engine scene loadFromString
      if (!loadSuccess && cesdk.engine && cesdk.engine.scene && typeof cesdk.engine.scene.loadFromString === 'function') {
        try {
          await cesdk.engine.scene.loadFromString(sceneText);
          loadSuccess = true;
        } catch (err) {
          console.warn("Failed loading with engine method:", err.message);
        }
      }
      
      if (!loadSuccess) {
        throw new Error("Could not load template. Please try a different template or refresh the page.");
      }
      
      // Find placeholders in the scene
      const findPlaceholders = () => {
        try {
          const scene = cesdk.engine.scene.get();
          if (!scene) return [];
          
          const blocks = [];
          const pages = cesdk.engine.block.getChildren(scene);
          
          // Get all blocks
          for (const page of pages) {
            try {
              const pageBlocks = cesdk.engine.block.getChildren(page);
              blocks.push(...pageBlocks);
            } catch (e) {
              console.warn("Could not get children of page:", e);
            }
          }
          
          // Filter for named blocks
          const placeholders = [];
          
          for (const blockId of blocks) {
            try {
              const name = cesdk.engine.block.getName(blockId);
              const type = cesdk.engine.block.getType(blockId);
              
              // Add text blocks and specific named graphic blocks
              if (type === "//ly.img.ubq/text" || 
                  name === "Person" || 
                  name === "Athlete" || 
                  name === "Background") {
                
                placeholders.push({
                  id: blockId,
                  name: name || "Untitled",
                  type: type,
                  fillType: type === "//ly.img.ubq/graphic" ? "//ly.img.ubq/fill/image" : null,
                  defaultContent: ""
                });
              }
            } catch (e) {
              // Skip blocks with errors
            }
          }
          
          return placeholders;
        } catch (e) {
          console.error("Error finding placeholders:", e);
          return [];
        }
      };
      
      // Get placeholders
      const detectedPlaceholders = findPlaceholders();
      
      // Parse template placeholder data
      let parsedPlaceholderData = template.placeholder_data;
      if (typeof parsedPlaceholderData === 'string') {
        try {
          parsedPlaceholderData = JSON.parse(parsedPlaceholderData);
        } catch (e) {
          console.error("Failed to parse placeholder_data JSON:", e);
          parsedPlaceholderData = [];
        }
      }
      
      if (!Array.isArray(parsedPlaceholderData)) {
        parsedPlaceholderData = [];
      }
      
      // Use detected placeholders if none in metadata
      if (parsedPlaceholderData.length === 0 && detectedPlaceholders.length > 0) {
        parsedPlaceholderData = detectedPlaceholders;
      }
      
      // Add fallback if still no placeholders
      if (parsedPlaceholderData.length === 0) {
        parsedPlaceholderData = [{
          name: "Person",
          type: "//ly.img.ubq/graphic",
          fillType: "//ly.img.ubq/fill/image",
          defaultContent: ""
        }];
      }
      
      // Initialize form data
      const initialFormData = {};
      const fields = [];
      
      parsedPlaceholderData.forEach(p => {
        initialFormData[p.name] = p.defaultContent || '';
        
        if (p.type === "//ly.img.ubq/text") {
          fields.push({
            name: p.name,
            type: 'text',
            defaultValue: p.defaultContent || ''
          });
        } else if (p.type === "//ly.img.ubq/graphic" && 
                 (p.name === "Person" || p.name === "Athlete")) {
          fields.push({
            name: p.name,
            type: 'image',
            defaultValue: ''
          });
        }
      });
      
      setPlaceholderData(parsedPlaceholderData);
      setPlaceholderFields(fields);
      setFormData(initialFormData);
      setSelectedTemplate(template);
    } catch (err) {
      console.error("Error preparing template customization:", err);
      setError("Error preparing template: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Apply customizations to CESDK scene
  const applyCustomizations = async (placeholderData, replacementData, primaryImageUrl) => {
    if (!cesdk) {
      throw new Error("Editor is not initialized");
    }
    
    if (!cesdk.engine || !cesdk.engine.scene) {
      throw new Error("Scene API is not available");
    }
    
    const scene = cesdk.engine.scene.get();
    if (!scene) {
      throw new Error("No scene is loaded");
    }
    
    try {
      // Get all pages in the scene
      const pages = cesdk.engine.block.getChildren(scene);
      
      // Get all blocks from all pages
      const allBlocks = [];
      for (const pageId of pages) {
        try {
          const pageBlocks = cesdk.engine.block.getChildren(pageId);
          allBlocks.push(...pageBlocks);
        } catch (e) {
          console.warn(`Could not get children of page ${pageId}:`, e);
        }
      }
      
      // Process placeholders
      for (const placeholder of placeholderData) {
        // Find blocks with matching name
        const placeholderBlocks = allBlocks.filter(blockId => {
          try {
            return cesdk.engine.block.getName(blockId) === placeholder.name;
          } catch (e) {
            return false; // Skip invalid blocks
          }
        });
        
        for (const blockId of placeholderBlocks) {
          try {
            const blockType = cesdk.engine.block.getType(blockId);
            
            // Handle text blocks
            if (blockType === "//ly.img.ubq/text") {
              const text = replacementData[placeholder.name] || placeholder.defaultContent || "";
              cesdk.engine.block.setString(blockId, "text/text", text);
              continue;
            }
            
            // Handle image blocks
            if (blockType === "//ly.img.ubq/graphic") {
              // Only process if this is a Person/Athlete placeholder or has a matching name in replacementData
              if (placeholder.name === "Person" || placeholder.name === "Athlete" || replacementData[placeholder.name]) {
                let imageUrl = replacementData[placeholder.name] || "";
                
                // Use primary image for Person placeholders
                if ((placeholder.name === "Person" || placeholder.name === "Athlete") && primaryImageUrl) {
                  imageUrl = primaryImageUrl;
                }
                
                if (!imageUrl) {
                  imageUrl = placeholder.defaultContent || "";
                  if (!imageUrl) continue; // Skip if no image URL available
                }
                
                // Different approaches to set the image:
                let imageApplied = false;
                
                // APPROACH 1: Try to get the fill and check what type it is
                try {
                  // Check if block supports fill
                  if (cesdk.engine.block.supportsFill && cesdk.engine.block.supportsFill(blockId)) {
                    // Get the fill block ID
                    let fillBlockId = null;
                    try {
                      fillBlockId = cesdk.engine.block.getFill(blockId);
                    } catch (e) {
                      fillBlockId = cesdk.engine.block.createFill("image");
                      cesdk.engine.block.setFill(blockId, fillBlockId);
                    }
                    
                    if (fillBlockId && cesdk.engine.block.isValid(fillBlockId)) {
                      const fillType = cesdk.engine.block.getType(fillBlockId);
                      
                      if (fillType === "//ly.img.ubq/fill/image") {
                        // Try setting the image by sourceSet first
                        try {
                          cesdk.engine.block.setSourceSet(fillBlockId, "fill/image/sourceSet", [
                            {
                              uri: imageUrl,
                              width: 1080,
                              height: 720,
                            },
                          ]);
                          imageApplied = true;
                        } catch (sourceSetErr) {
                          // Try the older imageFileURI method
                          try {
                            cesdk.engine.block.setString(fillBlockId, "fill/image/imageFileURI", imageUrl);
                            imageApplied = true;
                          } catch (uriErr) {
                            // Both methods failed, continue to next approach
                          }
                        }
                      } else {
                        // Not an image fill, replace it
                        const newFillBlockId = cesdk.engine.block.createFill("image");
                        
                        try {
                          cesdk.engine.block.setSourceSet(newFillBlockId, "fill/image/sourceSet", [
                            {
                              uri: imageUrl,
                              width: 1080,
                              height: 720,
                            },
                          ]);
                        } catch (e) {
                          cesdk.engine.block.setString(newFillBlockId, "fill/image/imageFileURI", imageUrl);
                        }
                        
                        cesdk.engine.block.setFill(blockId, newFillBlockId);
                        imageApplied = true;
                      }
                    }
                  }
                } catch (err) {
                  // Approach 1 failed, continue to next approach
                }
                
                // APPROACH 2: Direct sourceSet on the block (for newer CESDK versions)
                if (!imageApplied) {
                  try {
                    await cesdk.engine.block.setSourceSet(blockId, "fill/image/sourceSet", [
                      {
                        uri: imageUrl,
                        width: 1080,
                        height: 720,
                      },
                    ]);
                    imageApplied = true;
                  } catch (err) {
                    // Approach 2 failed, continue to next approach
                  }
                }
                
                // APPROACH 3: Try to use placeholder API
                if (!imageApplied) {
                  try {
                    if (cesdk.engine.block.setPlaceholderImage) {
                      await cesdk.engine.block.setPlaceholderImage(blockId, imageUrl);
                      imageApplied = true;
                    }
                  } catch (err) {
                    // Approach 3 failed as well
                  }
                }
                
                // Final status
                if (imageApplied) {
                  // Try to disable placeholder behavior if supported
                  try {
                    if (cesdk.engine.block.supportsPlaceholderBehavior && 
                        cesdk.engine.block.supportsPlaceholderBehavior(blockId)) {
                      cesdk.engine.block.setPlaceholderBehaviorEnabled(blockId, false);
                    }
                  } catch (e) {
                    // Ignore placeholder behavior errors
                  }
                } else {
                  console.error(`Failed to apply image to ${placeholder.name}`);
                }
              }
            }
          } catch (err) {
            console.error(`Error processing block:`, err);
          }
        }
      }
    } catch (err) {
      console.error("Error in applyCustomizations:", err);
      throw err;
    }
  };

  // Submit customization
  const handleSubmitCustomization = async () => {
    if (!cesdk || !selectedTemplate) {
      setError("Editor not ready or no template selected");
      return;
    }
    
    // Check if we have a primary image when needed
    if (!primaryImage && placeholderFields.some(f => f.type === 'image')) {
      setError("Please select a primary image first.");
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setExportedImage(null);
    setCurrentExport(null);
    
    try {
      // Apply customizations to the template
      await applyCustomizations(placeholderData, formData, primaryImage);
      
      // Export the customized scene
      const scene = cesdk.engine.scene.get();
      const blob = await cesdk.engine.block.export(scene, "image/png", { 
        dpi: 300,
        transparency: true
      });
      
      // Create object URL for preview
      const exportUrl = URL.createObjectURL(blob);
      setExportedImage(exportUrl);
      
      // Save export to Supabase
      const exportFileName = `export-${selectedTemplate.id}-${Date.now()}.png`;
      
      const { error: uploadError } = await supabase.storage
        .from("playmakerpics-images")
        .upload(`exports/${user.id}/${exportFileName}`, blob, { 
          contentType: "image/png",
          upsert: true
        });
      
      if (uploadError) throw uploadError;
      
      // Get public URL
      const { publicUrl } = supabase.storage
        .from("playmakerpics-images")
        .getPublicUrl(`exports/${user.id}/${exportFileName}`).data;
      
      // Log export in database
      const { data: exportRecord, error: exportError } = await supabase.from("exports").insert({
        user_id: user.id,
        template_id: selectedTemplate.id,
        export_url: publicUrl,
        exported_at: new Date().toISOString(),
        is_saved: false
      }).select('id').single();
      
      if (exportError) throw exportError;
      
      // Set the current export information for the save button
      setCurrentExport({
        id: exportRecord.id,
        url: publicUrl,
        template_name: selectedTemplate.name
      });
      
    } catch (err) {
      console.error("Export error:", err);
      setError("Export failed: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // Save the current export as a custom image
  const handleSaveCustomImage = async () => {
    if (!currentExport) {
      setError("No image to save. Please generate a preview first.");
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Update the export record to mark it as saved
      const { data, error } = await supabase
        .from("exports")
        .update({ is_saved: true })
        .eq('id', currentExport.id)
        .select()
        .single();
      
      if (error) throw error;
      
      // Add the saved image to the list
      const template = templates.find(t => t.id === data.template_id);
      const savedImage = {
        ...data,
        template_name: template ? template.name : 'Custom Design'
      };
      
      setSavedImages(prev => [savedImage, ...prev]);
      
      // Show success message
      alert("Image saved successfully!");
    } catch (err) {
      setError("Failed to save image: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // View image in gallery modal
  const handleViewImage = (image) => {
    const index = savedImages.findIndex(img => img.id === image.id);
    if (index !== -1) {
      setCurrentGalleryIndex(index);
      setCurrentGalleryImage(image);
      setGalleryVisible(true);
    }
  };

  // Navigate to next image in gallery
  const handleNextImage = () => {
    if (savedImages.length > 1) {
      const nextIndex = (currentGalleryIndex + 1) % savedImages.length;
      setCurrentGalleryIndex(nextIndex);
      setCurrentGalleryImage(savedImages[nextIndex]);
    }
  };

  // Navigate to previous image in gallery
  const handlePrevImage = () => {
    if (savedImages.length > 1) {
      const prevIndex = (currentGalleryIndex - 1 + savedImages.length) % savedImages.length;
      setCurrentGalleryIndex(prevIndex);
      setCurrentGalleryImage(savedImages[prevIndex]);
    }
  };

  // Close the gallery modal
  const closeGallery = () => {
    setGalleryVisible(false);
    setCurrentGalleryImage(null);
  };

  // Handle keyboard navigation for gallery
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!galleryVisible) return;
      
      switch (e.key) {
        case 'ArrowRight':
          handleNextImage();
          break;
        case 'ArrowLeft':
          handlePrevImage();
          break;
        case 'Escape':
          closeGallery();
          break;
        default:
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [galleryVisible, currentGalleryIndex, savedImages.length]);

  if (!user) return null;

  return (
    <div className="dashboard-container">
      {/* Modern Navigation Bar */}
      <nav className="dashboard-nav">
        <div className="nav-content">
          <div className="nav-brand">
            <h1>PlayMaker Pics</h1>
          </div>
          <div className="nav-actions">
            <button className="upload-btn" onClick={() => document.getElementById('file-upload').click()}>
              <span className="icon">+</span>
              Upload Image
            </button>
            <button className="logout-btn" onClick={handleLogout}>
              <span className="icon">↪</span>
              Logout
            </button>
          </div>
        </div>
      </nav>

      {/* Hidden Dashboard Editor for CESDK initialization */}
      <div style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: '1px', overflow: 'hidden' }}>
        <DashboardEditor onEditorReady={handleEditorReady} />
      </div>

      {/* Main Content Area */}
      <main className="dashboard-main">
        {/* Welcome Section */}
        <section className="welcome-section">
          <h2>Welcome back, {user?.user_metadata?.given_name || 'Creator'}</h2>
          <p>Your creative journey continues</p>
        </section>

        {/* Quick Actions */}
        <section className="quick-actions">
          <div className="action-card" onClick={() => document.getElementById('file-upload').click()}>
            <div className="card-icon">📸</div>
            <h3>Upload New Image</h3>
            <p>Start with a fresh canvas</p>
          </div>
          <div className="action-card" onClick={() => setShowTemplateBrowser(true)}>
            <div className="card-icon">🎨</div>
            <h3>Browse Templates</h3>
            <p>Find inspiration</p>
          </div>
        </section>

        {/* Image Gallery */}
        <section className="gallery-section">
          <h2>Your Images</h2>
          <div className="gallery-grid">
            {sourceImages.map((image, index) => (
              <div key={index} className="gallery-item">
                <div className="image-container">
                  <img src={image.url} alt={image.name} />
                  <div className="image-overlay">
                    <button 
                      className={`primary-btn ${primaryImage === image.url ? 'active' : ''}`}
                      onClick={() => handleSetPrimary(image.url)}
                    >
                      {primaryImage === image.url ? 'Primary' : 'Set as Primary'}
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => handleDeleteImage(image.name)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Templates Section */}
        <section className="templates-section">
          <h2>Recent Templates</h2>
          {!cesdk && (
            <div className="editor-loading-message">
              <p>Editor is initializing... Templates will be available shortly.</p>
              <div className="loading-spinner-small"></div>
            </div>
          )}
          <div className="templates-grid">
            {templates.slice(0, 4).map((template) => (
              <div 
                key={template.id} 
                className={`template-card ${!cesdk ? 'disabled' : ''}`}
                onClick={() => cesdk ? handleCustomizeTemplate(template) : setError("Editor is still initializing. Please wait a moment and try again.")}
              >
                <div className="template-thumbnail">
                  <img src={template.thumbnail_url} alt={template.name} />
                  <div className="template-overlay">
                    <span>{cesdk ? "Customize" : "Loading Editor..."}</span>
                  </div>
                </div>
                <div className="template-info">
                  <h3>{template.name}</h3>
                  <p>{template.style_category || 'General'} • {template.style_subcategory || 'Template'}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* My Custom Images Section */}
        <section className="saved-images-section">
          <h2>My Custom Images</h2>
          {savedImages.length === 0 ? (
            <div className="no-saved-images">
              <p>You haven't saved any custom images yet. Customize a template and save it to see it here!</p>
            </div>
          ) : (
            <div className="saved-images-grid">
              {savedImages.map((image) => (
                <div 
                  key={image.id} 
                  className="saved-image-card"
                  onClick={() => handleViewImage(image)}
                >
                  <div className="saved-image-thumbnail">
                    <img src={image.export_url} alt={image.template_name} />
                    <div className="image-overlay">
                      <div className="image-info-overlay">
                        <h4>{image.template_name}</h4>
                        <p>{new Date(image.exported_at).toLocaleDateString()}</p>
                      </div>
                      <div className="image-actions">
                        <a 
                          href={image.export_url} 
                          download={`${image.template_name.replace(/\s+/g, '-')}-${new Date(image.exported_at).toISOString().split('T')[0]}.png`}
                          className="download-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Download
                        </a>
                        <button 
                          className="view-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewImage(image);
                          }}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Hidden File Input */}
      <input
        type="file"
        id="file-upload"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Loading Overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Processing your request...</p>
        </div>
      )}

      {/* Error Toast */}
      {error && (
        <div className="error-toast">
          <p>{error}</p>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Template customization modal */}
      {selectedTemplate && (
        <div className="customization-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Customize {selectedTemplate.name}</h2>
              <button
                className="close-button"
                onClick={() => {
                  setSelectedTemplate(null);
                  setExportedImage(null);
                }}
              >×</button>
            </div>
            
            <div className="modal-body">
              <div className="customization-form">
                <h3>Customization Options</h3>
                
                {placeholderFields.length === 0 ? (
                  <p>No customizable elements found in this template.</p>
                ) : (
                  <>
                    {/* Text fields */}
                    {placeholderFields.filter(field => field.type === 'text').map(field => (
                      <div key={field.name} className="form-group">
                        <label>{field.name}</label>
                        <input
                          type="text"
                          name={field.name}
                          value={formData[field.name] || ''}
                          onChange={handleInputChange}
                          placeholder={`Enter ${field.name}`}
                        />
                      </div>
                    ))}
                  
                    {/* Image fields */}
                    {placeholderFields.filter(field => field.type === 'image').length > 0 && (
                      <div className="form-group">
                        <label>Athlete Image</label>
                        {primaryImage ? (
                          <div className="primary-image-preview">
                            <img src={primaryImage} alt="Primary" />
                            <div className="image-info">
                              <p>Using primary image</p>
                              <p className="image-hint">You can change this in the Image Gallery section</p>
                            </div>
                          </div>
                        ) : (
                          <div className="no-image-warning">
                            <p>Please select a primary image first</p>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
                
                <div className="form-actions">
                  <button
                    className="primary-button"
                    onClick={handleSubmitCustomization}
                    disabled={isLoading || !primaryImage && placeholderFields.some(f => f.type === 'image')}
                  >
                    {isLoading ? 'Processing...' : exportedImage ? 'Update Preview' : 'Generate Preview'}
                  </button>
                  
                  {exportedImage && (
                    <>
                      <button
                        className="download-button"
                        onClick={() => {
                          const link = document.createElement("a");
                          link.href = exportedImage;
                          link.download = `${selectedTemplate.name.replace(/\s+/g, '-')}-${Date.now()}.png`;
                          link.click();
                        }}
                      >
                        Download
                      </button>
                      
                      <button
                        className="save-button"
                        onClick={handleSaveCustomImage}
                        disabled={isLoading}
                      >
                        Save to My Images
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              <div className="preview-section">
                <h3>Preview</h3>
                <div className="preview-container">
                  {isLoading ? (
                    <div className="preview-loading">
                      <div className="loading-spinner-small"></div>
                      <p>Generating preview...</p>
                    </div>
                  ) : exportedImage ? (
                    <img 
                      src={exportedImage} 
                      alt="Exported Preview" 
                      className="preview-image"
                    />
                  ) : (
                    <div className="preview-placeholder">
                      <p>Click "Generate Preview" to see your customized template</p>
                      <img 
                        src={selectedTemplate.thumbnail_url} 
                        alt="Template thumbnail" 
                        className="template-thumbnail-preview"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Gallery Modal */}
      {galleryVisible && currentGalleryImage && (
        <div className="gallery-modal">
          <div className="gallery-content">
            <button className="gallery-close" onClick={closeGallery}>×</button>
            
            <div className="gallery-image-container">
              <img src={currentGalleryImage.export_url} alt={currentGalleryImage.template_name} />
            </div>
            
            <div className="gallery-info">
              <h3>{currentGalleryImage.template_name}</h3>
              <p>Created on {new Date(currentGalleryImage.exported_at).toLocaleDateString()}</p>
            </div>
            
            {savedImages.length > 1 && (
              <div className="gallery-navigation">
                <button className="nav-button prev-button" onClick={handlePrevImage}>
                  ←
                </button>
                <div className="gallery-counter">
                  {currentGalleryIndex + 1} / {savedImages.length}
                </div>
                <button className="nav-button next-button" onClick={handleNextImage}>
                  →
                </button>
              </div>
            )}
            
            <a 
              href={currentGalleryImage.export_url} 
              download={`${currentGalleryImage.template_name.replace(/\s+/g, '-')}-${Date.now()}.png`}
              className="gallery-download-button"
            >
              Download Image
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;